// React
import { useDropzone, ErrorCode } from "react-dropzone";
import { useCallback, useState } from "react";
import { t } from "i18next";

// Constants
import {
  SUPPORTED_IMAGE_TYPES,
  ACCEPTED_IMAGE_TYPES_LABEL,
  MAX_FILE_SIZE,
  MIN_FILE_SIZE,
} from "Constants/FileConstants";

// MUI
import { Alert, Paper, Stack, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

// Styles
import styles from "./styles";

/**
 * @brief DropzoneArea for file upload
 *
 * @param title The title text to display for file upload
 * @param handleUpload Function that processes the files on upload
 */
const DropzoneArea = ({ title, handleUpload }) => {
  const [warning, setWarning] = useState("");

  const onDrop = useCallback(
    (files) => {
      if (files?.length <= 0) {
        return;
      }

      handleUpload(files);
      setWarning("");
    },
    [handleUpload]
  );

  const onDropRejected = useCallback((fileRejections) => {
    if (!fileRejections.length) return;

    const err = fileRejections[0].errors[0];
    switch (err.code) {
      case ErrorCode.FileInvalidType:
        setWarning(
          `File "${fileRejections[0].file.name}" is not a supported type. ${ACCEPTED_IMAGE_TYPES_LABEL}`
        );
        break;
      case ErrorCode.FileTooLarge:
        setWarning(`File "${fileRejections[0].file.name}" is too large.`);
        break;
      case ErrorCode.FileTooSmall:
        setWarning(`File "${fileRejections[0].file.name}" is too small.`);
        break;
      default:
        setWarning(`File "${fileRejections[0].file.name}" was rejected.`);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    useFsAccessApi: true,
    onDrop,
    onDropRejected,
    accept: SUPPORTED_IMAGE_TYPES,
    maxFiles: 1,
    minSize: MIN_FILE_SIZE,
    maxSize: MAX_FILE_SIZE,
  });

  return (
    <Stack spacing={2} alignItems={"center"} sx={{ mb: 1.5 }}>
      <Stack spacing={1}>
        <Typography variant="h5" fontWeight="medium" textAlign="center">
          {title}
        </Typography>
      </Stack>
      <Paper {...getRootProps()} sx={styles.paper}>
        <input {...getInputProps()} />
        <Stack alignItems="center">
          <CloudUploadOutlinedIcon sx={{ fontSize: "4rem" }} />

          <Typography variant="subtitle1">
            {t("dropzone.dropImagesInstructions")}
          </Typography>

          <Typography variant="subtitle2">
            {ACCEPTED_IMAGE_TYPES_LABEL}
          </Typography>
        </Stack>
      </Paper>
      {warning && (
        <Alert severity="warning" onClose={() => setWarning("")}>
          {warning}
        </Alert>
      )}
    </Stack>
  );
};

export default DropzoneArea;
